import { ENV_CONFIG } from '../../../config/environment';
import { supabase } from '../../../lib/supabase';

import { invokeStripeBilling } from './stripeBillingClient';

// Stripe types (simplified - in real implementation, would import from @stripe/stripe-js)
interface StripeCustomer {
  id: string;
  email: string;
  name?: string;
  metadata?: Record<string, string>;
}

interface StripeSubscription {
  id: string;
  customer?: string | { id: string };
  status: 'active' | 'canceled' | 'incomplete' | 'incomplete_expired' | 'past_due' | 'trialing' | 'unpaid';
  current_period_start: number;
  current_period_end: number;
  cancel_at_period_end: boolean;
  canceled_at?: number;
  trial_start?: number;
  trial_end?: number;
  items: {
    data: Array<{
      id: string;
      price: {
        id: string;
        recurring: {
          interval: 'month' | 'year';
        };
        unit_amount: number;
      };
    }>;
  };
  latest_invoice?: {
    amount_due?: number;
    payment_intent?: {
      id?: string;
      client_secret?: string;
      status?: string;
    };
  };
}

interface StripePaymentIntent {
  id: string;
  client_secret: string;
  amount: number;
  currency: string;
  status: 'requires_payment_method' | 'requires_confirmation' | 'requires_action' | 'processing' | 'requires_capture' | 'canceled' | 'succeeded';
  customer?: string;
  metadata?: Record<string, string>;
}

interface StripeWebhookEvent {
  id: string;
  type: string;
  data: {
    object: StripeWebhookObject;
  };
  created: number;
}

type StripeWebhookObject = StripeSubscription | StripePaymentIntent | Record<string, unknown>;
type StripeMetadata = Record<string, unknown>;
type EdgeFunctionPayload = Record<string, unknown>;
type InvoiceWebhookPayload = { id: string };

interface EdgeFunctionResponse<T> {
  data: T | null;
  error: { message?: string } | null;
}

export class StripeService {
  // In-memory store for mock subscriptions (maps subscriptionId to subscription data)
  private mockSubscriptions: Map<string, StripeSubscription> = new Map();

  /**
   * Call edge function with retry logic and enhanced error handling
   */
  private async callEdgeFunctionWithRetry(
    payload: EdgeFunctionPayload,
    maxRetries: number = 3,
    delayMs: number = 1000
  ): Promise<EdgeFunctionResponse<EdgeFunctionPayload>> {
    let lastError: unknown = null;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        console.log(`🔄 [StripeService] Edge function attempt ${attempt}/${maxRetries}`, {
          payload: { ...payload, subscriptionItemId: payload.subscriptionItemId?.slice(-8) },
          timestamp: new Date().toISOString()
        });

        const startTime = Date.now();
        const response = await supabase.functions.invoke<EdgeFunctionPayload>('stripe-modify-subscription', {
          body: payload
        });

        const duration = Date.now() - startTime;
        console.log(`⏱️ [StripeService] Edge function completed in ${duration}ms`, {
          attempt,
          hasData: !!response.data,
          hasError: !!response.error
        });

        // Log successful response details
        if (response.data && !response.error) {
          console.log(`✅ [StripeService] Edge function success on attempt ${attempt}`, {
            subscriptionId: response.data.subscription?.id,
            newStatus: response.data.subscription?.status,
            duration
          });
          return response;
        }

        // Handle response with error data but no error flag (API responded with error)
        if (response.data && response.data.error) {
          console.warn(`⚠️ [StripeService] Edge function returned error data on attempt ${attempt}:`, response.data);
          return response; // Return this to let caller handle the specific error
        }

        // Handle actual error response
        if (response.error) {
          console.warn(`⚠️ [StripeService] Edge function error on attempt ${attempt}:`, response.error);
          lastError = response.error;

          // Don't retry on these specific errors
          if (response.error.message?.includes('Subscription is incomplete') ||
              response.error.message?.includes('Subscription cannot be modified')) {
            console.log(`🚫 [StripeService] Non-retryable error, aborting retries`);
            return response;
          }

          // Wait before retry (exponential backoff)
          if (attempt < maxRetries) {
            const waitTime = delayMs * Math.pow(2, attempt - 1);
            console.log(`⏳ [StripeService] Waiting ${waitTime}ms before retry ${attempt + 1}`);
            await new Promise(resolve => setTimeout(resolve, waitTime));
          }
        }

      } catch (error) {
        console.error(`❌ [StripeService] Edge function exception on attempt ${attempt}:`, error);
        lastError = error;

        if (attempt < maxRetries) {
          const waitTime = delayMs * Math.pow(2, attempt - 1);
          console.log(`⏳ [StripeService] Waiting ${waitTime}ms before retry ${attempt + 1}`);
          await new Promise(resolve => setTimeout(resolve, waitTime));
        }
      }
    }

    console.error(`💥 [StripeService] All ${maxRetries} edge function attempts failed`, lastError);
    throw new Error(`Edge function failed after ${maxRetries} attempts: ${lastError?.message || 'Unknown error'}`);
  }

  /**
   * Verify database synchronization after plan change
   */
  private async verifyDatabaseSync(subscriptionId: string, expectedPriceId: string): Promise<boolean> {
    try {
      console.log(`🔍 [StripeService] Verifying database sync for subscription ${subscriptionId}`);

      // Map price ID to subscription type for verification
      const priceToTierMap: Record<string, string> = {
        'price_1S8jcfEVwi2hWMVu2aNSNFSH': 'premium',
        'price_1S8jchEVwi2hWMVuDdp6whWF': 'premium',
        'price_1S8jciEVwi2hWMVurPWfCgiZ': 'premium',
        'price_1S8jckEVwi2hWMVu9W4KtUiz': 'premium',
        'price_1S8jcmEVwi2hWMVujrvoC2xo': 'premium',
        'price_1S8jcoEVwi2hWMVu64aLtj93': 'premium',
        'price_1S8jcpEVwi2hWMVuYB7Fp36m': 'premium',
        'price_1S8jcrEVwi2hWMVuQdtm3vHS': 'premium',
        'price_1S8jctEVwi2hWMVuy0snw8Z7': 'premium',
        'price_1S8jcvEVwi2hWMVugj4SymFu': 'premium'
      };

      const expectedSubscriptionType = priceToTierMap[expectedPriceId];
      if (!expectedSubscriptionType) {
        console.warn(`⚠️ [StripeService] Unknown price ID for verification: ${expectedPriceId}`);
        return false;
      }

      // Query database to check current subscription type
      const { data, error } = await supabase
        .from('user_subscriptions')
        .select('subscription_type, updated_at')
        .eq('provider_subscription_id', subscriptionId)
        .single();

      if (error) {
        console.error(`❌ [StripeService] Database query error during verification:`, error);
        return false;
      }

      if (!data) {
        console.error(`❌ [StripeService] No subscription found in database for ${subscriptionId}`);
        return false;
      }

      const isSynced = data.subscription_type === expectedSubscriptionType;
      console.log(`🔍 [StripeService] Database sync verification:`, {
        subscriptionId,
        expectedType: expectedSubscriptionType,
        actualType: data.subscription_type,
        isSynced,
        lastUpdated: data.updated_at
      });

      return isSynced;

    } catch (error) {
      console.error(`❌ [StripeService] Database verification failed:`, error);
      return false;
    }
  }

  /**
   * Attempt to recover database synchronization by manually updating the subscription type
   */
  private async recoverDatabaseSync(subscriptionId: string, expectedPriceId: string): Promise<boolean> {
    try {
      console.log(`🔧 [StripeService] Attempting database sync recovery for ${subscriptionId}`);

      // Map price ID to subscription type for recovery
      const priceToTierMap: Record<string, string> = {
        'price_1S8jcfEVwi2hWMVu2aNSNFSH': 'premium',
        'price_1S8jchEVwi2hWMVuDdp6whWF': 'premium',
        'price_1S8jciEVwi2hWMVurPWfCgiZ': 'premium',
        'price_1S8jckEVwi2hWMVu9W4KtUiz': 'premium',
        'price_1S8jcmEVwi2hWMVujrvoC2xo': 'premium',
        'price_1S8jcoEVwi2hWMVu64aLtj93': 'premium',
        'price_1S8jcpEVwi2hWMVuYB7Fp36m': 'premium',
        'price_1S8jcrEVwi2hWMVuQdtm3vHS': 'premium',
        'price_1S8jctEVwi2hWMVuy0snw8Z7': 'premium',
        'price_1S8jcvEVwi2hWMVugj4SymFu': 'premium'
      };

      const correctSubscriptionType = priceToTierMap[expectedPriceId];
      if (!correctSubscriptionType) {
        console.error(`❌ [StripeService] Cannot recover: unknown price ID ${expectedPriceId}`);
        return false;
      }

      // Determine billing interval from price ID
      const billingInterval = expectedPriceId.includes('Monthly') ||
                            expectedPriceId.endsWith('o') || // Monthly price IDs end with 'o'
                            expectedPriceId.endsWith('Z7') ||
                            expectedPriceId.endsWith('m') ? 'monthly' : 'annual';

      // Manually update the database subscription
      const { data, error } = await supabase
        .from('user_subscriptions')
        .update({
          subscription_type: correctSubscriptionType,
          billing_interval: billingInterval,
          updated_at: new Date().toISOString(),
          metadata: {
            recovery_performed: true,
            recovery_timestamp: new Date().toISOString(),
            recovery_reason: 'edge_function_sync_failure',
            recovered_from_price_id: expectedPriceId
          }
        })
        .eq('provider_subscription_id', subscriptionId)
        .select();

      if (error) {
        console.error(`❌ [StripeService] Database recovery update failed:`, error);
        return false;
      }

      if (!data || data.length === 0) {
        console.error(`❌ [StripeService] No subscription found for recovery: ${subscriptionId}`);
        return false;
      }

      console.log(`✅ [StripeService] Database sync recovery successful:`, {
        subscriptionId,
        updatedTo: correctSubscriptionType,
        billingInterval,
        recoveryTimestamp: new Date().toISOString()
      });

      // Verify the recovery worked
      const isNowSynced = await this.verifyDatabaseSync(subscriptionId, expectedPriceId);

      if (isNowSynced) {
        console.log(`🎉 [StripeService] Recovery verification passed`);
        return true;
      } else {
        console.error(`❌ [StripeService] Recovery verification failed despite successful update`);
        return false;
      }

    } catch (error) {
      console.error(`❌ [StripeService] Database sync recovery failed:`, error);
      return false;
    }
  }

  /**
   * Public method to manually trigger database sync recovery (for debugging/admin use)
   */
  async manualSyncRecovery(subscriptionId: string, expectedPriceId: string): Promise<boolean> {
    console.log(`🔧 [StripeService] Manual sync recovery requested for ${subscriptionId}`);

    const isCurrentlySynced = await this.verifyDatabaseSync(subscriptionId, expectedPriceId);

    if (isCurrentlySynced) {
      console.log(`✅ [StripeService] Subscription is already synced correctly`);
      return true;
    }

    console.log(`⚠️ [StripeService] Subscription requires sync recovery, attempting...`);
    const recovered = await this.recoverDatabaseSync(subscriptionId, expectedPriceId);

    if (recovered) {
      console.log(`🎉 [StripeService] Manual sync recovery successful`);
    } else {
      console.error(`❌ [StripeService] Manual sync recovery failed`);
    }

    return recovered;
  }

  /**
   * Create a customer in Stripe
   */
  async createCustomer(userId: string, email: string, name?: string): Promise<string> {
    try {
      // If payments are disabled or in mock mode, return mock customer ID
      if (!ENV_CONFIG.MONETIZATION.SUBSCRIPTION_ENABLED || ENV_CONFIG.DEV.ENABLE_MOCK_PAYMENTS) {
        const mockCustomerId = `cus_mock_${userId.slice(0, 8)}`;
        
        // Store mock customer ID in user profile
        await supabase
          .from('user_profiles')
          .update({ stripe_customer_id: mockCustomerId })
          .eq('id', userId);

        console.log(`Created mock Stripe customer: ${mockCustomerId}`);
        return mockCustomerId;
      }

      const { customer } = await invokeStripeBilling<{ customer: StripeCustomer }>('create_customer', {
        userId,
        email,
        name,
      });

      // Store customer ID in user profile
      await supabase
        .from('user_profiles')
        .update({ stripe_customer_id: customer.id })
        .eq('id', userId);

      console.log(`Created Stripe customer: ${customer.id} for user: ${userId}`);
      return customer.id;
    } catch (error) {
      console.error('Failed to create Stripe customer:', error);
      throw error;
    }
  }

  /**
   * Create a subscription
   */
  async createSubscription(customerId: string, priceId: string): Promise<StripeSubscription> {
    try {
      // If payments are disabled or in mock mode, return mock subscription
      if (!ENV_CONFIG.MONETIZATION.SUBSCRIPTION_ENABLED || ENV_CONFIG.DEV.ENABLE_MOCK_PAYMENTS) {
        return this.createMockSubscription(customerId, priceId);
      }

      const { subscription } = await invokeStripeBilling<{ subscription: StripeSubscription }>('create_subscription', {
        customerId,
        priceId,
      });

      console.log(`Created Stripe subscription: ${subscription.id} for customer: ${customerId}`);
      return subscription;
    } catch (error) {
      console.error('Failed to create subscription:', error);
      throw error;
    }
  }

  /**
   * Cancel a subscription
   */
  async cancelSubscription(subscriptionId: string, cancelAtPeriodEnd: boolean = true): Promise<StripeSubscription> {
    try {
      // If payments are disabled or in mock mode, return mock cancellation
      if (!ENV_CONFIG.MONETIZATION.SUBSCRIPTION_ENABLED || ENV_CONFIG.DEV.ENABLE_MOCK_PAYMENTS) {
        return this.cancelMockSubscription(subscriptionId, cancelAtPeriodEnd);
      }

      const { subscription } = await invokeStripeBilling<{ subscription: StripeSubscription }>('cancel_subscription', {
        subscriptionId,
        cancelAtPeriodEnd,
      });

      console.log(`${cancelAtPeriodEnd ? 'Scheduled cancellation for' : 'Canceled'} subscription: ${subscriptionId}`);
      return subscription;
    } catch (error) {
      console.error('Failed to cancel subscription:', error);
      throw error;
    }
  }

  /**
   * Modify subscription to change plan with proration
   */
  async modifySubscription(
    subscriptionId: string,
    newPriceId: string,
    prorationEnabled: boolean = true,
    newBillingInterval?: 'monthly' | 'annual'
  ): Promise<StripeSubscription> {
    const startTime = Date.now();
    console.log(`🔄 [StripeService] Starting subscription modification`, {
      subscriptionId,
      newPriceId,
      prorationEnabled,
      newBillingInterval,
      timestamp: new Date().toISOString()
    });

    try {
      // Get current subscription to get the subscription item ID
      const currentSubscription = await this.getSubscription(subscriptionId);
      if (!currentSubscription) {
        throw new Error(`Subscription ${subscriptionId} not found`);
      }

      const currentSubscriptionItemId = currentSubscription.items.data[0]?.id;
      if (!currentSubscriptionItemId) {
        throw new Error('No subscription item found to modify');
      }

      console.log(`🔍 [StripeService] Pre-modification state:`, {
        currentPriceId: currentSubscription.items.data[0]?.price?.id,
        currentStatus: currentSubscription.status,
        subscriptionItemId: currentSubscriptionItemId
      });

      // Call backend API to modify subscription via Stripe with retry logic
      const edgeFunctionResult = await this.callEdgeFunctionWithRetry({
        subscriptionId,
        subscriptionItemId: currentSubscriptionItemId,
        newPriceId,
        // Use 'always_invoice' to immediately create and charge proration
        prorationBehavior: prorationEnabled ? 'always_invoice' : 'none',
        newBillingInterval
      }, 3);

      const { data, error } = edgeFunctionResult;

      // Log for debugging if needed
      if (error) {
        console.log('🔍 [StripeService] Edge Function error occurred');
      }

      // Check if we have data despite an error (common with 400 status responses)
      if (data && typeof data === 'object' && data.error) {
        console.log('🔍 [StripeService] Found error data in response data:', data);

        // Handle specific subscription status issues from response data
        if (data.error === 'Subscription is incomplete') {
          throw new Error(data.userMessage || 'Please complete your payment first before changing your plan.');
        }

        if (data.error === 'Subscription cannot be modified') {
          throw new Error(data.userMessage || 'Your subscription cannot be modified at this time.');
        }

        throw new Error(data.userMessage || `Subscription modification failed: ${data.error}`);
      }

      if (error) {
        console.error('🔴 [StripeService] Edge Function error details:', error);

        // Try to extract the actual response from the error
        let errorData = null;
        try {
          // Try multiple approaches to extract the error response

          // Method 1: Check if edgeFunctionResult.response exists and try to get text from it
          if (edgeFunctionResult.response && typeof edgeFunctionResult.response.text === 'function') {
            const responseText = await edgeFunctionResult.response.text();
            if (responseText && responseText.startsWith('{')) {
              errorData = JSON.parse(responseText);
            }
          }

          // Method 2: Check error.context
          if (!errorData && error.context) {
            if (error.context.body) {
              errorData = JSON.parse(error.context.body);
            } else if (error.context.json) {
              errorData = error.context.json;
            } else if (typeof error.context === 'object') {
              errorData = error.context;
            }
          }

          // Method 3: Check error.details
          if (!errorData && error.details) {
            errorData = error.details;
          }

          // Method 4: Parse JSON from error message
          if (!errorData && error.message && error.message.includes('{')) {
            const jsonMatch = error.message.match(/\{.*\}/);
            if (jsonMatch) {
              errorData = JSON.parse(jsonMatch[0]);
            }
          }
        } catch (parseError) {
          console.error('Failed to parse error response:', parseError);
        }

        // Log extracted error data for debugging
        if (errorData) {
          console.log('🔍 [StripeService] Extracted user-friendly error:', errorData.userMessage || errorData.error);
        }

        // Handle specific subscription status issues from error response
        if (errorData?.error === 'Subscription is incomplete') {
          throw new Error(errorData.userMessage || 'Please complete your payment first before changing your plan.');
        }

        if (errorData?.error === 'Subscription cannot be modified') {
          throw new Error(errorData.userMessage || 'Your subscription cannot be modified at this time.');
        }

        throw new Error(`Stripe subscription modification failed: ${error.message}`);
      }

      if (!data || !data.success) {
        console.error('🔴 [StripeService] Edge Function returned error:', data);

        // Handle specific subscription status issues
        if (data?.error === 'Subscription is incomplete') {
          throw new Error(data.userMessage || 'Please complete your payment first before changing your plan.');
        }

        if (data?.error === 'Subscription cannot be modified') {
          throw new Error(data.userMessage || 'Your subscription cannot be modified at this time.');
        }

        throw new Error(`Stripe subscription modification failed: ${data?.error || 'Unknown error'}`);
      }

      const modifiedSubscription = data.subscription;
      const totalDuration = Date.now() - startTime;

      console.log(`🎉 [StripeService] Subscription modification completed successfully`, {
        subscriptionId,
        newPriceId,
        totalDuration,
        attempts: 'edge function succeeded'
      });

      // Verify database synchronization after successful modification
      console.log(`🔍 [StripeService] Starting database verification...`);

      // Wait a moment for edge function database update to complete
      await new Promise(resolve => setTimeout(resolve, 500));

      const isSynced = await this.verifyDatabaseSync(subscriptionId, newPriceId);

      if (!isSynced) {
        console.warn(`⚠️ [StripeService] Database sync verification failed, attempting recovery...`);

        // Attempt to trigger sync recovery
        const recovered = await this.recoverDatabaseSync(subscriptionId, newPriceId);

        if (!recovered) {
          console.error(`❌ [StripeService] Database recovery failed for subscription ${subscriptionId}`);
          // Note: We don't throw here because Stripe modification succeeded
          // The subscription will work, but may show wrong plan in UI until manually fixed
        } else {
          console.log(`✅ [StripeService] Database recovery successful`);
        }
      } else {
        console.log(`✅ [StripeService] Database sync verification passed`);
      }

      console.log(`🏁 [StripeService] Subscription modification process complete`, {
        subscriptionId,
        newPriceId,
        totalDuration: Date.now() - startTime,
        databaseSynced: isSynced
      });

      return modifiedSubscription;
    } catch (error) {
      const totalDuration = Date.now() - startTime;
      const errorMessage = error instanceof Error ? error.message : 'Unknown Stripe modification error';
      console.error(`❌ [StripeService] Subscription modification failed after ${totalDuration}ms:`, {
        subscriptionId,
        newPriceId,
        error: errorMessage,
        totalDuration
      });
      throw error;
    }
  }

  /**
   * Create a payment intent for one-time purchases
   */
  async createPaymentIntent(customerId: string, amountUsd: number, metadata: StripeMetadata): Promise<StripePaymentIntent> {
    try {
      // If payments are disabled or in mock mode, return mock payment intent
      if (!ENV_CONFIG.MONETIZATION.MARKETPLACE_ENABLED || ENV_CONFIG.DEV.ENABLE_MOCK_PAYMENTS) {
        return this.createMockPaymentIntent(customerId, amountUsd, metadata);
      }

      const { paymentIntent } = await invokeStripeBilling<{ paymentIntent: StripePaymentIntent }>('create_payment_intent', {
        customerId,
        amountUsd,
        metadata,
      });

      console.log(`Created payment intent: ${paymentIntent.id} for $${amountUsd}`);
      return paymentIntent;
    } catch (error) {
      console.error('Failed to create payment intent:', error);
      throw error;
    }
  }

  /**
   * Retrieve a subscription
   */
  async getSubscription(subscriptionId: string): Promise<StripeSubscription | null> {
    try {
      // If payments are disabled or in mock mode, return mock subscription
      if (!ENV_CONFIG.MONETIZATION.SUBSCRIPTION_ENABLED || ENV_CONFIG.DEV.ENABLE_MOCK_PAYMENTS) {
        return this.getMockSubscription(subscriptionId);
      }

      const { subscription } = await invokeStripeBilling<{ subscription: StripeSubscription | null }>('get_subscription', {
        subscriptionId,
      });

      return subscription;
    } catch (error) {
      console.error('Failed to get subscription:', error);
      return null;
    }
  }

  /**
   * Handle webhook events
   */
  async handleWebhookEvent(event: StripeWebhookEvent): Promise<void> {
    try {
      console.log(`Processing Stripe webhook: ${event.type}`);

      switch (event.type) {
        case 'customer.subscription.created':
        case 'customer.subscription.updated':
          await this.handleSubscriptionUpdate(event.data.object as StripeSubscription);
          break;
        case 'customer.subscription.deleted':
          await this.handleSubscriptionCancellation(event.data.object as StripeSubscription);
          break;
        case 'payment_intent.succeeded':
          await this.handlePaymentSuccess(event.data.object as StripePaymentIntent);
          break;
        case 'payment_intent.payment_failed':
          await this.handlePaymentFailure(event.data.object as StripePaymentIntent);
          break;
        case 'invoice.payment_succeeded':
          await this.handleInvoicePaymentSuccess(event.data.object);
          break;
        case 'invoice.payment_failed':
          await this.handleInvoicePaymentFailure(event.data.object);
          break;
        default:
          console.log(`Unhandled webhook event type: ${event.type}`);
      }
    } catch (error) {
      console.error('Failed to handle webhook event:', error);
      throw error;
    }
  }

  /**
   * Verify webhook signature (security)
   */
  verifyWebhookSignature(payload: string, signature: string, secret: string): boolean {
    try {
      // In real implementation, would use Stripe's signature verification
      // For now, return true in development mode
      if (ENV_CONFIG.DEV.ENABLE_MOCK_PAYMENTS) {
        return true;
      }

      // Mock verification logic
      const expectedSignature = `sha256=${Buffer.from(payload + secret).toString('base64')}`;
      return signature === expectedSignature;
    } catch (error) {
      console.error('Failed to verify webhook signature:', error);
      return false;
    }
  }

  /**
   * Get customer by ID
   */
  async getCustomer(customerId: string): Promise<StripeCustomer | null> {
    try {
      // If payments are disabled or in mock mode, return mock customer
      if (!ENV_CONFIG.MONETIZATION.ENABLED || ENV_CONFIG.DEV.ENABLE_MOCK_PAYMENTS) {
        return this.getMockCustomer(customerId);
      }

      const { customer } = await invokeStripeBilling<{ customer: StripeCustomer | null }>('get_customer', {
        customerId,
      });

      return customer;
    } catch (error) {
      console.error('Failed to get customer:', error);
      return null;
    }
  }

  // Private helper methods

  private async handleSubscriptionUpdate(subscription: StripeSubscription, billingInterval?: 'monthly' | 'annual'): Promise<void> {
    try {
      // Handle customer ID - it could be a string, an object, or undefined
      let customerId: string | undefined;
      if (typeof subscription.customer === 'string') {
        customerId = subscription.customer;
      } else if (subscription.customer && typeof subscription.customer === 'object') {
        customerId = subscription.customer.id;
      }

      // If customer ID is missing, try to get it from our database using subscription ID
      if (!customerId) {
        console.log('Subscription missing customer ID, fetching from database:', subscription.id);
        const { data: existingSubscription, error: _subError } = await supabase
          .from('user_subscriptions')
          .select('provider_customer_id')
          .eq('provider_subscription_id', subscription.id)
          .single();

        if (existingSubscription?.provider_customer_id) {
          customerId = existingSubscription.provider_customer_id;
          console.log('Found customer ID from database:', customerId);
        } else {
          console.error('Could not find customer ID for subscription:', subscription.id);
          return;
        }
      }

      // Find user by customer ID
      const { data: userProfile, error } = await supabase
        .from('user_profiles')
        .select('id')
        .eq('stripe_customer_id', customerId)
        .single();

      if (error || !userProfile) {
        console.error('User not found for customer:', customerId);
        return;
      }

      // Update subscription in database
      await supabase
        .from('user_subscriptions')
        .upsert({
          user_id: userProfile.id,
          provider_subscription_id: subscription.id,
          provider_customer_id: customerId,
          status: subscription.status,
          current_period_start: new Date(subscription.current_period_start * 1000).toISOString(),
          current_period_end: new Date(subscription.current_period_end * 1000).toISOString(),
          cancel_at_period_end: subscription.cancel_at_period_end,
          cancelled_at: subscription.canceled_at ? new Date(subscription.canceled_at * 1000).toISOString() : null,
          trial_ends_at: subscription.trial_end ? new Date(subscription.trial_end * 1000).toISOString() : null,
          billing_interval: billingInterval || 'monthly', // Update billing interval if provided
          updated_at: new Date().toISOString(),
          metadata: {
            stripe_subscription_id: subscription.id,
            stripe_customer_id: customerId,
            updated_via: 'webhook'
          }
        }, {
          onConflict: 'user_id,provider_subscription_id'
        });

      console.log(`Updated subscription ${subscription.id} for user ${userProfile.id}`);
    } catch (error) {
      console.error('Failed to handle subscription update:', error);
      throw error;
    }
  }

  private async handleSubscriptionCancellation(subscription: StripeSubscription): Promise<void> {
    try {
      // Update subscription status to canceled
      await supabase
        .from('user_subscriptions')
        .update({
          status: 'canceled',
          cancelled_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('provider_subscription_id', subscription.id);

      console.log(`Canceled subscription ${subscription.id}`);
    } catch (error) {
      console.error('Failed to handle subscription cancellation:', error);
      throw error;
    }
  }

  private async handlePaymentSuccess(paymentIntent: StripePaymentIntent): Promise<void> {
    try {
      // Update purchase status to completed
      await supabase
        .from('user_purchases')
        .update({
          status: 'completed',
          completed_at: new Date().toISOString()
        })
        .eq('payment_provider_id', paymentIntent.id);

      console.log(`Payment succeeded for intent ${paymentIntent.id}`);
    } catch (error) {
      console.error('Failed to handle payment success:', error);
      throw error;
    }
  }

  private async handlePaymentFailure(paymentIntent: StripePaymentIntent): Promise<void> {
    try {
      // Update purchase status to failed
      await supabase
        .from('user_purchases')
        .update({
          status: 'failed',
          failure_reason: 'Payment failed'
        })
        .eq('payment_provider_id', paymentIntent.id);

      console.log(`Payment failed for intent ${paymentIntent.id}`);
    } catch (error) {
      console.error('Failed to handle payment failure:', error);
      throw error;
    }
  }

  private async handleInvoicePaymentSuccess(invoice: InvoiceWebhookPayload): Promise<void> {
    try {
      // Handle successful invoice payment (subscription renewal)
      console.log(`Invoice payment succeeded: ${invoice.id}`);
    } catch (error) {
      console.error('Failed to handle invoice payment success:', error);
      throw error;
    }
  }

  private async handleInvoicePaymentFailure(invoice: InvoiceWebhookPayload): Promise<void> {
    try {
      // Handle failed invoice payment (subscription payment failure)
      console.log(`Invoice payment failed: ${invoice.id}`);
    } catch (error) {
      console.error('Failed to handle invoice payment failure:', error);
      throw error;
    }
  }

  // Mock methods for development and testing

  private async mockStripeApiCall<T>(endpoint: string, method: string, data?: StripeMetadata): Promise<T> {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 100));

    // Mock successful response based on endpoint
    if (endpoint === 'customers' && method === 'POST') {
      return {
        id: `cus_${Date.now()}`,
        email: typeof data?.email === 'string' ? data.email : '',
        name: typeof data?.name === 'string' ? data.name : '',
        metadata: (data?.metadata as Record<string, string> | undefined)
      } as T;
    }

    if (endpoint === 'subscriptions' && method === 'POST') {
      const customerId = typeof data?.customer === 'string' ? data.customer : 'cus_mock_unknown';
      const items = Array.isArray(data?.items) ? data.items as Array<{ price?: string }> : [];
      const priceId = items[0]?.price || 'price_mock_premium_monthly';
      return this.createMockSubscription(customerId, priceId) as T;
    }

    if (endpoint === 'payment_intents' && method === 'POST') {
      const customerId = typeof data?.customer === 'string' ? data.customer : 'cus_mock_unknown';
      const amount = typeof data?.amount === 'number' ? data.amount / 100 : 0;
      const metadata = (data?.metadata && typeof data.metadata === 'object'
        ? data.metadata
        : {}) as StripeMetadata;
      return this.createMockPaymentIntent(customerId, amount, metadata) as T;
    }

    // Handle subscription GET requests
    if (endpoint.startsWith('subscriptions/') && method === 'GET') {
      const subscriptionId = endpoint.split('/')[1];
      return this.getMockSubscription(subscriptionId) as T;
    }

    // Handle subscription UPDATE requests (for modifications)
    if (endpoint.startsWith('subscriptions/') && method === 'PATCH') {
      const subscriptionId = endpoint.split('/')[1];
      const currentSubscription = this.getMockSubscription(subscriptionId);
      if (!currentSubscription) {
        throw new Error(`Subscription ${subscriptionId} not found`);
      }

      // Update subscription with new data
      const updatedSubscription = {
        ...currentSubscription,
        ...data,
        updated: Math.floor(Date.now() / 1000)
      };

      return updatedSubscription as T;
    }

    console.warn(`Mock API call not implemented for ${method} ${endpoint} - returning null`);
    return null as T;
  }

  private createMockSubscription(customerId: string, priceId: string): StripeSubscription {
    const now = Math.floor(Date.now() / 1000);
    const oneMonth = 30 * 24 * 60 * 60;

    const subscription: StripeSubscription = {
      id: `sub_mock_${Date.now()}`,
      customer: customerId,
      status: 'active',
      current_period_start: now,
      current_period_end: now + oneMonth,
      cancel_at_period_end: false,
      items: {
        data: [{
          id: `si_mock_${Date.now()}`,
          price: {
            id: priceId,
            recurring: { interval: 'month' },
            unit_amount: 499 // $4.99
          }
        }]
      }
    };

    // Store the subscription in our mock store
    this.mockSubscriptions.set(subscription.id, subscription);
    console.log(`Stored mock subscription ${subscription.id} for customer ${customerId}`);

    return subscription;
  }

  private cancelMockSubscription(subscriptionId: string, cancelAtPeriodEnd: boolean): StripeSubscription {
    const now = Math.floor(Date.now() / 1000);
    const oneMonth = 30 * 24 * 60 * 60;

    return {
      id: subscriptionId,
      customer: `cus_mock_${Date.now()}`,
      status: cancelAtPeriodEnd ? 'active' : 'canceled',
      current_period_start: now,
      current_period_end: now + oneMonth,
      cancel_at_period_end: cancelAtPeriodEnd,
      canceled_at: cancelAtPeriodEnd ? undefined : now,
      items: {
        data: [{
          id: `si_mock_default_${Date.now()}`,
          price: {
            id: 'price_mock',
            recurring: { interval: 'month' },
            unit_amount: 699
          }
        }]
      }
    };
  }

  private createMockPaymentIntent(customerId: string, amountUsd: number, metadata: StripeMetadata): StripePaymentIntent {
    return {
      id: `pi_mock_${Date.now()}`,
      client_secret: `pi_mock_${Date.now()}_secret_mock`,
      amount: Math.round(amountUsd * 100),
      currency: 'usd',
      status: 'succeeded',
      customer: customerId,
      metadata
    };
  }

  private getMockSubscription(subscriptionId: string): StripeSubscription | null {
    if (!subscriptionId.startsWith('sub_mock_')) {
      return null;
    }

    // Try to retrieve from our mock store first
    const storedSubscription = this.mockSubscriptions.get(subscriptionId);
    if (storedSubscription) {
      console.log(`Retrieved mock subscription ${subscriptionId} for customer ${storedSubscription.customer}`);
      return storedSubscription;
    }

    // If not found, create a generic one (for backwards compatibility)
    console.log(`Mock subscription ${subscriptionId} not found in store, creating generic one`);
    const mockSub = this.createMockSubscription('cus_mock_generic', 'price_mock_123');
    mockSub.id = subscriptionId; // Override with requested ID

    return mockSub;
  }


  private getMockCustomer(customerId: string): StripeCustomer | null {
    if (!customerId.startsWith('cus_mock_')) {
      return null;
    }

    return {
      id: customerId,
      email: 'mock@example.com',
      name: 'Mock Customer',
      metadata: { userId: 'mock-user-id' }
    };
  }
}

// Singleton instance
export const stripeService = new StripeService();

// Default export
export default stripeService;
